import { useRef, useEffect, useState } from "react";
import type { Member, MemberAccountTransaction, StaffUser, BusinessSettings } from "@/services/types";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { formatINRNumber, formatDate } from "@/utils/format";
import { paymentModeLabel } from "@/pages/collection/components/labels";
import { usersService, settingsService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { roleLabel } from "@/config/roles";

interface AccountReceiptModalProps {
  open: boolean;
  onClose: () => void;
  member: Member;
  transaction: MemberAccountTransaction | null;
}

export function AccountReceiptModal({ open, onClose, member, transaction }: AccountReceiptModalProps) {
  const receiptRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const [businessSettings, setBusinessSettings] = useState<BusinessSettings | null>(null);
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [slot, setSlot] = useState<"top" | "middle" | "bottom" | "all">("top");

  useEffect(() => {
    if (!open) return;
    usersService
      .listUsers()
      .then((u) => setStaffUsers(u))
      .catch(() => setStaffUsers([]));
    settingsService
      .getBusinessSettings()
      .then(setBusinessSettings)
      .catch(() => {});
  }, [open]);

  if (!transaction) return null;

  const isDeposit = transaction.type === "DEPOSIT";
  const isWithdrawal = transaction.type === "WITHDRAWAL";
  const title = isDeposit
    ? "Advance Deposit Receipt"
    : isWithdrawal
    ? "Member Cashout Voucher"
    : "Transaction Receipt";

  const docNumber = transaction.receiptNo || transaction.referenceNo || "—";

  // Real user lookup to get true name and designation
  const rawCollected = (transaction.creatorName || transaction.createdBy || "").trim();
  const matchedUser = staffUsers.find(
    (u) =>
      u.name.toLowerCase() === rawCollected.toLowerCase() ||
      u.id === rawCollected ||
      u.email.toLowerCase() === rawCollected.toLowerCase()
  );

  const collectorName =
    matchedUser?.name ||
    (rawCollected && rawCollected !== "Staff" && rawCollected !== "Staff User"
      ? rawCollected
      : user?.name || "Super Admin");

  const collectorDesignation = matchedUser?.role
    ? roleLabel(matchedUser.role)
    : user && user.name.toLowerCase() === collectorName.toLowerCase() && user.role
    ? roleLabel(user.role)
    : user?.role
    ? roleLabel(user.role)
    : "";

  const collectorText = collectorDesignation
    ? `${collectorName}(${collectorDesignation})`
    : collectorName;

  // Group / enrollment details
  const primaryEnrollment = member.enrollments && member.enrollments.length > 0 ? member.enrollments[0] : null;
  const entryNumber = primaryEnrollment?.ticketNumber || "1";
  const mkfsCode = primaryEnrollment?.groupName
    ? primaryEnrollment.groupName
    : member.memberCode || "—";
  const startDate = primaryEnrollment?.joinedOn || member.createdAt;

  // Financial values
  const installmentAmount = primaryEnrollment?.monthlyInstallment ?? transaction.amount;
  const totalMembers =
    member.enrollments && member.enrollments.length > 0
      ? member.enrollments.length > 1
        ? `${member.enrollments.length} Groups`
        : "3"
      : "—";
  const cycleNo = isDeposit || isWithdrawal ? "—" : "1";
  const groupName = primaryEnrollment?.groupName || "Advance Account";
  const schemeValue = primaryEnrollment?.monthlyInstallment
    ? primaryEnrollment.monthlyInstallment * 3
    : transaction.amount * 3 > 0
    ? transaction.amount * 3
    : 120000;

  const handlePrint = () => {
    // Create an isolated iframe for printing this voucher/receipt specifically
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) return;

    // Collect all stylesheets and style blocks from the host page
    const styles = Array.from(document.querySelectorAll("link[rel='stylesheet'], style"))
      .map((el) => el.outerHTML)
      .join("\n");

    const singleSlipHtml = document.getElementById("account-receipt-single-slip")?.innerHTML || "";

    const contentHtml =
      slot === "all"
        ? `
          <div class="flex flex-col justify-between space-y-2 print:h-[285mm] print:space-y-0">
            ${singleSlipHtml}
            <div class="cut-line flex items-center justify-center gap-2 py-0.5 text-[9.5px] text-foreground-400 print:text-[8.5px] print:text-foreground-500">
              <span class="text-[12px]">✂</span>
              <span class="flex-1 border-b border-dashed border-foreground-400"></span>
              <span class="uppercase tracking-widest">Cut Along Line</span>
              <span class="flex-1 border-b border-dashed border-foreground-400"></span>
            </div>
            ${singleSlipHtml}
            <div class="cut-line flex items-center justify-center gap-2 py-0.5 text-[9.5px] text-foreground-400 print:text-[8.5px] print:text-foreground-500">
              <span class="text-[12px]">✂</span>
              <span class="flex-1 border-b border-dashed border-foreground-400"></span>
              <span class="uppercase tracking-widest">Cut Along Line</span>
              <span class="flex-1 border-b border-dashed border-foreground-400"></span>
            </div>
            ${singleSlipHtml}
          </div>
        `
        : `
          ${slot === "middle" ? '<div class="slot-spacer h-[95mm]"></div>' : ""}
          ${slot === "bottom" ? '<div class="slot-spacer h-[190mm]"></div>' : ""}
          ${singleSlipHtml}
        `;

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title} - ${docNumber}</title>
          ${styles}
          <style>
            @page {
              size: A4 portrait;
              margin: 5mm 6mm;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
            }
            .receipt-a4-sheet {
              display: block !important;
              width: 100% !important;
              height: 285mm !important;
              max-height: 285mm !important;
            }
            .slot-spacer {
              display: block !important;
            }
            .mk-receipt-box {
              border: 1.5px solid #000 !important;
              box-shadow: none !important;
              padding: 3mm 4mm !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              height: 94mm !important;
              max-height: 94mm !important;
              margin-bottom: 0 !important;
              box-sizing: border-box !important;
            }
            .cut-line {
              margin: 1mm 0 !important;
            }
          </style>
        </head>
        <body>
          <div class="receipt-a4-sheet">
            ${contentHtml}
          </div>
        </body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1500);
      }
    }, 250);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handlePrint]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex flex-wrap items-center justify-between gap-3 pr-6">
          <div className="flex items-center gap-2">
            <span className="font-bold text-foreground-950">{title}</span>
            <span className="tnum font-mono text-xs text-foreground-500 font-semibold">{docNumber}</span>
          </div>
          {/* A4 Print slot switcher */}
          <div className="no-print flex items-center gap-1 text-xs">
            <span className="text-foreground-500 font-normal">A4 Layout:</span>
            <div className="flex items-center rounded-md border border-background-200 bg-background-100 p-0.5">
              <button
                type="button"
                title="Print on top 1/3 slot of A4"
                className={`cursor-pointer rounded px-2 py-1 font-medium transition-colors ${
                  slot === "top" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                }`}
                onClick={() => setSlot("top")}
              >
                Top (1/3)
              </button>
              <button
                type="button"
                title="Print on middle 1/3 slot"
                className={`cursor-pointer rounded px-2 py-1 font-medium transition-colors ${
                  slot === "middle" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                }`}
                onClick={() => setSlot("middle")}
              >
                Middle (1/3)
              </button>
              <button
                type="button"
                title="Print on bottom 1/3 slot"
                className={`cursor-pointer rounded px-2 py-1 font-medium transition-colors ${
                  slot === "bottom" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                }`}
                onClick={() => setSlot("bottom")}
              >
                Bottom (1/3)
              </button>
              <button
                type="button"
                title="Print all 3 receipts on full A4 page"
                className={`cursor-pointer rounded px-2 py-1 font-medium transition-colors ${
                  slot === "all" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                }`}
                onClick={() => setSlot("all")}
              >
                All 3 on A4
              </button>
            </div>
          </div>
        </div>
      }
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<i className="ri-printer-line" />}
            onClick={handlePrint}
          >
            Print Receipt
          </Button>
        </div>
      }
    >
      <div className="overflow-x-auto py-1">
        {/* Hidden container used to grab single slip HTML for iframe printing */}
        <div id="account-receipt-single-slip" className="hidden">
          <ReceiptSlipContent
            member={member}
            transaction={transaction}
            docNumber={docNumber}
            isDeposit={isDeposit}
            isWithdrawal={isWithdrawal}
            collectorText={collectorText}
            primaryEnrollment={primaryEnrollment}
            entryNumber={entryNumber}
            mkfsCode={mkfsCode}
            startDate={startDate}
            installmentAmount={installmentAmount}
            totalMembers={totalMembers}
            cycleNo={cycleNo}
            groupName={groupName}
            schemeValue={schemeValue}
            businessSettings={businessSettings}
          />
        </div>

        {/* Screen Preview Container */}
        <div ref={receiptRef} className="mx-auto w-full max-w-[210mm]">
          {slot === "all" ? (
            <div className="space-y-3">
              <ReceiptSlipContent
                member={member}
                transaction={transaction}
                docNumber={docNumber}
                isDeposit={isDeposit}
                isWithdrawal={isWithdrawal}
                collectorText={collectorText}
                primaryEnrollment={primaryEnrollment}
                entryNumber={entryNumber}
                mkfsCode={mkfsCode}
                startDate={startDate}
                installmentAmount={installmentAmount}
                totalMembers={totalMembers}
                cycleNo={cycleNo}
                groupName={groupName}
                schemeValue={schemeValue}
                businessSettings={businessSettings}
              />
              <CutLine />
              <ReceiptSlipContent
                member={member}
                transaction={transaction}
                docNumber={docNumber}
                isDeposit={isDeposit}
                isWithdrawal={isWithdrawal}
                collectorText={collectorText}
                primaryEnrollment={primaryEnrollment}
                entryNumber={entryNumber}
                mkfsCode={mkfsCode}
                startDate={startDate}
                installmentAmount={installmentAmount}
                totalMembers={totalMembers}
                cycleNo={cycleNo}
                groupName={groupName}
                schemeValue={schemeValue}
                businessSettings={businessSettings}
              />
              <CutLine />
              <ReceiptSlipContent
                member={member}
                transaction={transaction}
                docNumber={docNumber}
                isDeposit={isDeposit}
                isWithdrawal={isWithdrawal}
                collectorText={collectorText}
                primaryEnrollment={primaryEnrollment}
                entryNumber={entryNumber}
                mkfsCode={mkfsCode}
                startDate={startDate}
                installmentAmount={installmentAmount}
                totalMembers={totalMembers}
                cycleNo={cycleNo}
                groupName={groupName}
                schemeValue={schemeValue}
                businessSettings={businessSettings}
              />
            </div>
          ) : (
            <>
              <ReceiptSlipContent
                member={member}
                transaction={transaction}
                docNumber={docNumber}
                isDeposit={isDeposit}
                isWithdrawal={isWithdrawal}
                collectorText={collectorText}
                primaryEnrollment={primaryEnrollment}
                entryNumber={entryNumber}
                mkfsCode={mkfsCode}
                startDate={startDate}
                installmentAmount={installmentAmount}
                totalMembers={totalMembers}
                cycleNo={cycleNo}
                groupName={groupName}
                schemeValue={schemeValue}
                businessSettings={businessSettings}
              />
              <div className="no-print mt-3 space-y-2">
                <CutLine />
                <div className="rounded border border-dashed border-background-300 bg-background-50/60 p-3 text-center text-xs text-foreground-500">
                  A4 Slot Mode: <strong>{slot.toUpperCase()}</strong>. Remaining part of the sheet will be fed or left blank for subsequent prints.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function CutLine() {
  return (
    <div className="cut-line flex items-center justify-center gap-2 py-0.5 text-[9.5px] text-foreground-400 print:text-[8.5px] print:text-foreground-500">
      <span className="text-[12px]">✂</span>
      <span className="flex-1 border-b border-dashed border-foreground-400"></span>
      <span className="uppercase tracking-widest">Cut Along Line</span>
      <span className="flex-1 border-b border-dashed border-foreground-400"></span>
    </div>
  );
}

interface SlipProps {
  member: Member;
  transaction: MemberAccountTransaction;
  docNumber: string;
  isDeposit: boolean;
  isWithdrawal: boolean;
  collectorText: string;
  primaryEnrollment: any;
  entryNumber: string;
  mkfsCode: string;
  startDate: string;
  installmentAmount: number;
  totalMembers: string;
  cycleNo: string;
  groupName: string;
  schemeValue: number;
  businessSettings: BusinessSettings | null;
}

function ReceiptSlipContent({
  member,
  transaction,
  docNumber,
  isDeposit,
  isWithdrawal,
  collectorText,
  entryNumber,
  mkfsCode,
  startDate,
  installmentAmount,
  totalMembers,
  cycleNo,
  groupName,
  schemeValue,
  businessSettings,
}: SlipProps) {
  const { businessName, effectiveLogo } = useBranding();
  const pillTitle = isWithdrawal ? "WITHDRAWAL" : " ADVANCE PAYMENT RECEIPT";

  return (
    <div className="mk-receipt-box mx-auto flex w-full flex-col justify-between border-2 border-foreground-950 bg-white p-3 font-sans text-[11px] leading-tight text-foreground-950 print:border-[1.5px] print:p-2.5 print:text-[10.5px]">
      {/* Top Header */}
      <div className="flex items-center justify-between border-b border-foreground-950/20 pb-1 text-[10.5px] font-semibold text-foreground-800 print:text-[9.5px]">
        <span className="invisible select-none">GSTN. NO. 07ARYPK6255G1Z7</span>
        <div className="flex items-center gap-2">
          <span className="rounded border border-foreground-950 px-2.5 py-0.5 text-[11px] font-bold tracking-widest text-foreground-950 print:text-[10px]">
            {pillTitle}
          </span>
        </div>
        <span>{businessSettings?.registrationNumber || "GSTN. NO. 07ARYPK6255G1Z7"}</span>
      </div>

      {/* Main Company Branding Header */}
      <div className="flex items-center justify-between gap-2 py-2 print:py-2">
        {/* Logo on Left */}
        <div className="flex-none">
          <img
            src={effectiveLogo}
            alt={businessName}
            className="h-12 max-h-12 w-auto max-w-[54px] object-contain print:h-11 print:max-h-11 rounded"
            style={{ maxHeight: "48px" }}
          />
        </div>

        {/* Title & Addresses Centered */}
        <div className="flex-1 text-center min-w-0">
          <h1 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-[#3b2d6a] mb-1 print:mb-1.5 print:text-[24px] print:font-black print:leading-tight">
            {businessName || "M.K. FINANCIAL SERVICES (REGD.)"}
          </h1>
          <p className="whitespace-nowrap text-[10.5px] font-medium text-foreground-800 leading-normal tracking-tight mb-1 print:mb-1 print:text-[10px] print:tracking-tight">
            Regd. Office :- {businessSettings?.address ? `${businessSettings.address}, ${businessSettings.city} ${businessSettings.state} - ${businessSettings.pinCode}` : "D - 58, GALI NO.5, NEAR PANDEY STORE, RAJAPURI, UTTAM NAGAR, WEST DELHI - 110059(INDIA)"}
          </p>
          <div className="mt-1 flex items-center justify-between text-[11px] font-medium text-foreground-700 print:mt-1 print:text-[10px]">
            <span>E-Mail ID :- {businessSettings?.email || "mkfinancialservices333@gmail.com"}</span>
            <span>Mob. No. : {businessSettings?.primaryPhone ? [businessSettings.primaryPhone, businessSettings.alternatePhone].filter(Boolean).join(", ") : "9811498128, 9555577732"}</span>
          </div>
        </div>

        {/* Empty balance spacer to keep header centered */}
        <div className="w-12 flex-none print:w-11" />
      </div>

      {/* 3-Column Metadata Row with Outer and Inner Borders */}
      <div className="grid grid-cols-3 border-y border-foreground-950 text-[10.5px] font-medium print:text-[9.5px]">
        <div className="border-r border-foreground-950 px-2 py-0.5">
          <span>{isWithdrawal ? "Voucher No. :- " : "Receipt No. :- "}</span>
          <strong className="tnum font-bold text-foreground-950">{docNumber}</strong>
        </div>
        <div className="border-r border-foreground-950 px-2 py-0.5 text-center">
          <span>MKFS:- </span>
          <strong className="tnum font-bold text-foreground-950">{mkfsCode}</strong>
        </div>
        <div className="px-2 py-0.5 text-right">
          <span>{isWithdrawal ? "Voucher Date :- " : "Receipt Date :- "}</span>
          <strong className="tnum font-bold text-foreground-950">{formatDate(transaction.createdAt)}</strong>
        </div>
      </div>

      {/* Main 3-Column Body Section */}
      <div className="grid grid-cols-12 border-b border-foreground-950">
        {/* Column 1: Details of Subscribers */}
        <div className="col-span-5 flex flex-col justify-between border-r border-foreground-950 p-2 print:p-1.5">
          <div className="space-y-1">
            <p className="text-[10.5px] font-bold underline print:text-[9.5px]">Details of Member :-</p>
            <div className="grid grid-cols-3 gap-1 pt-0.5 text-[10.5px] print:text-[9.5px]">
              <span className="font-semibold">NAME :</span>
              <span className="col-span-2 font-bold uppercase">{member.fullName}</span>
            </div>
            <div className="grid grid-cols-3 gap-1 text-[10.5px] print:text-[9.5px]">
              <span className="font-semibold">MOB. NO. :</span>
              <span className="tnum col-span-2 font-medium">{member.primaryPhone}</span>
            </div>
            <div className="grid grid-cols-3 gap-1 text-[10.5px] print:text-[9.5px]">
              <span className="font-semibold">Ref. By :- </span>
              <span className="col-span-2 font-medium">
                {member.guardianName || "—"}
              </span>
            </div>
          </div>
          {/* Entry No. below details of subscribers */}
          {/* <div className="mt-1.5 border-t border-dashed border-foreground-950/25 pt-1 text-[10.5px] print:mt-1 print:pt-0.5 print:text-[9.5px]">
            <span className="font-semibold">Entry No. :- </span>
            <strong className="tnum font-bold text-foreground-950">{entryNumber}</strong>
          </div> */}
        </div>

        {/* Column 2: Cycle & Total Members */}
        <div className="col-span-2 flex flex-col justify-between border-r border-foreground-950 p-2 print:p-1.5">
          <div className="space-y-2">
            {/* <div>
              <p className="text-[10px] font-semibold text-foreground-800 print:text-[9px]">Cycle No. :-</p>
              <p className="tnum mt-0.5 text-sm font-extrabold leading-tight text-foreground-950 print:text-[12px]">{cycleNo}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-foreground-800 print:text-[9px]">Total Members :-</p>
              <p className="tnum mt-0.5 text-xs font-bold leading-tight text-foreground-950 print:text-[10.5px]">{totalMembers}</p>
            </div> */}
          </div>
          <p className="tnum mt-1 truncate text-[9.5px] font-medium text-foreground-600 print:text-[8.5px]">
            {groupName}
          </p>
        </div>

        {/* Column 3: In Rupees Financial Table */}
        <div className="col-span-5 p-2 print:p-1.5">
          <p className="mb-0.5 text-right text-[9.5px] font-semibold uppercase tracking-wider text-foreground-700 print:text-[8.5px]">
            ( IN RUPEES )
          </p>
          <div className="space-y-0.5 text-[10px] print:text-[9.5px]">
            <div className="flex justify-between">
              <span>{isDeposit ? " Amount" : isWithdrawal ? "Withdrawal Amount" : "Transaction Amount"}</span>
              <span className="tnum font-medium">
                {formatINRNumber(transaction.amount)}
              </span>
            </div>
             <div className="flex justify-between text-foreground-700">
              <span>{isWithdrawal ? "Deduction" : "Profit"}</span>
              <span className="tnum font-medium">—</span>
            </div>
           {/*  <div className="flex justify-between text-foreground-700">
              <span>Late Fee</span>
              <span className="tnum font-medium">—</span>
            </div>
            <div className="flex justify-between text-foreground-700">
              <span>Firm&apos;s</span>
              <span className="tnum font-medium">—</span>
            </div> */}
            <div className="flex justify-between border-y border-foreground-950 py-0.5 font-bold">
              <span>Total</span>
              <span className="tnum font-extrabold">{formatINRNumber(transaction.amount)}</span>
            </div>
            <div className="flex justify-between pt-0.5 font-semibold">
              <span>{isDeposit ? "Amount Deposit" : isWithdrawal ? "Amount Withdrawn" : "Amount Paid"}</span>
              <span className="tnum font-bold">{formatINRNumber(transaction.amount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Balance Amount Row */}
      <div className="flex justify-end border-b border-foreground-950 px-3 py-1 text-[11px] font-bold print:text-[10px]">
        <div className="flex w-5/12 justify-between">
          <span>Balance Amount</span>
          <span className="tnum font-extrabold">{formatINRNumber(transaction.balanceAfter)}</span>
        </div>
      </div>

      {/* Bottom Scheme Details & Authorized Signature Row */}
      <div className="grid grid-cols-12 pt-1.5 text-[10px] print:text-[9.5px]">
        {/* Left 7 sub-columns: Payment details */}
        <div className="col-span-7 flex flex-col justify-end border-r border-foreground-950/20 pb-0.5 pr-2">
          {/* <div className="flex justify-between">
            <div>
              <span className="font-semibold">Value Scheme: </span>
              <span className="tnum font-bold">
                {formatINRNumber(schemeValue)}
              </span>
            </div>
            <div>
              <span className="font-semibold">Loss Amount :- </span>
              <span className="tnum font-medium">0.00</span>
            </div>
          </div>
          <div className="flex justify-between">
            <div>
              <span className="font-semibold">Inst. Amount:- </span>
              <span className="tnum font-bold">
                {formatINRNumber(installmentAmount)}
              </span>
            </div> */}
         {/* </div> */}
          {/* Details of Payment & Collector */}
          <div className="flex flex-wrap items-center justify-between gap-1 pt-0.5">
            <div>
              <span className="font-semibold">Details of Payment :- </span>
              <span className="font-bold uppercase text-foreground-950">
                {paymentModeLabel(transaction.mode)}
              </span>
              {transaction.referenceNo ? (
                <span className="tnum ml-1 text-[9px] text-foreground-600">({transaction.referenceNo})</span>
              ) : null}
            </div>
            <div>
              <span className="font-semibold">{isWithdrawal ? "Paid By - " : "Collected By - "}</span>
              <span className="font-bold text-foreground-950">{collectorText}</span>
            </div>
          </div>
        </div>

        {/* Right signature column */}
        <div className="col-span-5 flex flex-col justify-between pl-3 text-right">
          <p className="text-[10px] font-bold tracking-tight text-foreground-950 print:text-[9px]">
            FOR M.K.FINANCIAL SERVICES ( REGD.)
          </p>
          <div className="pt-3 print:pt-2">
            <p className="text-[10px] font-bold text-foreground-900 print:text-[9px]">PROP. / AUTH. SIGN.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

